import React from "react";
import { BRAND } from "../lib/directorate";
import SovereignAnchor from "./SovereignAnchor";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

/**
 * EIGOO Lore Injection — high-fidelity typographic strike defining the
 * acronym (E·I·G·OO) as the brand's covenant. Each clause renders as a
 * full-width gold-gradient lockup with a serif tail.
 *
 * Mounted on the landing hub between the hero and the I-Want Engine so
 * every visitor encounters the doctrine before any CTA.
 */
export default function LoreInjection() {
    return (
        <section
            data-testid="lore-injection"
            className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 sm:py-28"
        >
            {/* Aurora wash + grain anchor */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 w-[80%] h-[60%] rounded-full blur-[160px] opacity-40 bg-[hsl(40_60%_45%/0.18)]" />
                <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage:
                            "linear-gradient(hsl(45 85% 68% / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(45 85% 68% / 0.4) 1px, transparent 1px)",
                        backgroundSize: "56px 56px",
                    }}
                />
                {/* Faint anchor watermark, dead-center */}
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[hsl(45_85%_68%)]"
                    style={{ opacity: 0.04 }}
                >
                    <SovereignAnchor size={520} />
                </div>
            </div>

            {/* Section eyebrow */}
            <div className="relative z-10 flex items-center gap-4 mb-14 max-w-3xl mx-auto">
                <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
                <div
                    className="text-[10px] uppercase tracking-[0.5em] text-[hsl(45_85%_68%)] font-mono"
                    data-testid="lore-eyebrow"
                >
                    The EIGOO Covenant · Acronym Doctrine
                </div>
                <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
            </div>

            {/* The acronym as a 4-line vertical strike */}
            <div
                className="relative z-10 max-w-5xl mx-auto space-y-3 sm:space-y-5"
                data-testid="lore-stack"
            >
                {BRAND.acronym.map((entry, idx) => (
                    <LoreLine
                        key={entry.letter}
                        ordinal={String(idx + 1).padStart(2, "0")}
                        letter={entry.letter}
                        verb={entry.verb}
                        clause={entry.clause}
                    />
                ))}
            </div>

            {/* Resolution / signature line */}
            <div
                className="relative z-10 mt-16 sm:mt-20 max-w-3xl mx-auto text-center"
                data-testid="lore-resolution"
            >
                <div className="flex items-center justify-center gap-3 text-[hsl(45_85%_68%)] mb-4">
                    <span className="h-px w-12 bg-[hsl(45_85%_68%)] opacity-50" />
                    <SovereignAnchor size={22} />
                    <span className="h-px w-12 bg-[hsl(45_85%_68%)] opacity-50" />
                </div>
                <p
                    className="text-xl sm:text-2xl text-foreground/85 leading-snug"
                    style={{ ...cormorant, fontStyle: "italic" }}
                >
                    "Four letters. One covenant. The Elite Infrastructure Foundry."
                </p>
                <div className="mt-3 text-[10px] uppercase tracking-[0.45em] text-muted-foreground font-mono">
                    — EIGOO · Directorate
                </div>
            </div>
        </section>
    );
}

function LoreLine({ ordinal, letter, verb, clause }) {
    // OO is two-character; render at slightly tighter scale so the row aligns.
    const isDouble = letter.length > 1;
    return (
        <div
            data-testid={`lore-line-${letter.toLowerCase()}`}
            className="group relative flex items-baseline gap-4 sm:gap-7 py-4 sm:py-5 border-b border-[hsl(40_40%_25%/0.25)] hover:border-[hsl(45_85%_68%/0.55)] transition-colors"
        >
            {/* Ordinal */}
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.4em] text-muted-foreground/60 font-mono w-8 shrink-0">
                {ordinal}
            </span>

            {/* The big letter */}
            <span
                className="font-heading gold-gradient-text leading-[0.85] tracking-tighter shrink-0"
                style={{
                    fontSize: isDouble
                        ? "clamp(3.25rem, 9.5vw, 8rem)"
                        : "clamp(3.75rem, 11vw, 9.5rem)",
                    letterSpacing: "-0.04em",
                    textShadow: "0 4px 40px hsl(40 60% 45% / 0.35)",
                }}
            >
                {letter}
            </span>

            {/* Clause */}
            <div className="flex-1 min-w-0 pb-2 sm:pb-4">
                <div
                    className="font-heading text-foreground tracking-tight leading-[1] uppercase"
                    style={{ fontSize: "clamp(1.5rem, 3.4vw, 3rem)" }}
                >
                    {verb}
                    <span
                        className="ml-3 inline align-baseline normal-case font-normal text-foreground/55"
                        style={{
                            ...cormorant,
                            fontStyle: "italic",
                            fontSize: "0.6em",
                            letterSpacing: "0",
                        }}
                    >
                        {clause}
                    </span>
                </div>
            </div>

            {/* Right gold rail accent on hover */}
            <span
                aria-hidden="true"
                className="absolute right-0 top-0 bottom-0 w-px bg-[hsl(45_85%_68%)] opacity-0 group-hover:opacity-60 transition-opacity"
            />
        </div>
    );
}
